<template>
  <div class="pb-8">
    <v-toolbar color="grey-darken-4" flat theme="dark" density="compact">
      <v-toolbar-title>
        {{ $t("home.user_monthly_summary.title") }}
      </v-toolbar-title>
    </v-toolbar>

    <v-card flat class="ma-2">
      <v-card-text>
        <!-- Filters row -->
        <v-row align="end" class="px-2 mb-2">
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="selectedUserId"
              :items="userItems"
              :label="$t('home.user_monthly_summary.select_employee')"
              :loading="usersLoading"
              variant="outlined"
              flat
              item-title="displayName"
              item-value="_id"
              persistent-hint
              color="red-darken-2"
              background-color="grey-lighten-4"
              clearable
               hide-details
            />
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <v-select
              v-model="monthNumber"
              :items="monthItems"
              :label="$t('home.user_monthly_summary.select_month')"
              variant="outlined"
              flat
              item-title="title"
              item-value="value"
              hide-details
              color="red-darken-2"
              background-color="grey-lighten-4"
            />
          </v-col>
          <v-col cols="6" sm="3" md="2">
            <v-select
              v-model="yearNumber"
              :items="yearItems"
              :label="$t('home.user_monthly_summary.select_year')"
              variant="outlined"
              flat
              item-title="title"
              item-value="value"
              hide-details
              color="red-darken-2"
              background-color="grey-lighten-4"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="d-flex align-center pb-1">
            <v-btn
              variant="text"
              icon
              :disabled="!canLoad"
              @click="fetchSummary"
            >
              <v-icon color="grey-darken-2">mdi-orbit-variant</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          border="bottom"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-alert
          v-else-if="!summary.loading && !hasData && !selectedUserId"
          type="info"
          variant="tonal"
          border="bottom"
          class="mb-4"
        >
          {{ $t("home.user_monthly_summary.no_user") }}
        </v-alert>

        <v-row v-if="summary.loading" justify="center" class="py-8">
          <v-progress-circular size="70" color="red" indeterminate />
        </v-row>

        <template v-else-if="hasData">
          <div v-if="summary.user" class="text-subtitle-2 mb-3 px-1">
            {{ summary.user.name }} {{ summary.user.surname }}
            <span v-if="selectedMonthLabel" class="user-summary-period">
              · {{ selectedMonthLabel }}
            </span>
          </div>

          <div class="summary-cards-grid mb-4">
            <v-card
              v-for="card in allSummaryCards"
              :key="card.key"
              :color="card.color"
              flat
              class="summary-stat-card"
            >
              <v-card-text class="pa-3">
                <div class="summary-stat-value">
                  {{ card.value }}
                </div>
                <div class="summary-stat-label">
                  {{ card.label }}
                </div>
              </v-card-text>
            </v-card>
          </div>

          <v-card flat color="grey-lighten-4" class="pa-2">
            <v-card-title class="text-subtitle-1">
              {{ $t("home.user_monthly_summary.by_status") }}
            </v-card-title>
            <div class="user-chart-wrap">
              <chart
                :key="`user-bar-${chartKey}`"
                type="bar"
                :data="barChartData"
                :height="barChartHeight"
                :extra-options="barChartExtraOptions"
              />
            </div>
          </v-card>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import Chart from "../chart.vue";

const DIGITALIZATION_STATUSES = "1.0,1.1,1.2,1.3,1.4,2.0";

function formatLocalMonthString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function parseLocalMonthString(str) {
  const [y, m] = str.split("-").map(Number);
  return new Date(y, m - 1, 1);
}

function currentMonthString() {
  return formatLocalMonthString(new Date());
}

const STATUS_SERIES = [
  {
    key: "InsertedTemporaryParts",
    label: "InsertedTemporaryParts",
    color: "rgba(156, 39, 176, 0.85)",
    borderColor: "rgba(156, 39, 176, 1)",
    cardColor: "purple-darken-1",
    translationKey: "part_status.inserted_temporary_parts",
  },
  {
    key: "PartsSentForApproval",
    label: "PartsSentForApproval",
    color: "rgba(33, 150, 243, 0.85)",
    borderColor: "rgba(33, 150, 243, 1)",
    cardColor: "blue-darken-1",
    translationKey: "part_status.parts_sent_for_approval",
  },
  {
    key: "PartsSentForReview",
    label: "PartsSentForReview",
    color: "rgba(255, 152, 0, 0.85)",
    borderColor: "rgba(255, 152, 0, 1)",
    cardColor: "orange-darken-2",
    translationKey: "part_status.parts_sent_for_review",
  },
  {
    key: "RefusedParts",
    label: "RefusedParts",
    color: "rgba(244, 67, 54, 0.85)",
    borderColor: "rgba(244, 67, 54, 1)",
    cardColor: "red-darken-1",
    translationKey: "part_status.refused_parts",
  },
  {
    key: "ApprovedParts",
    label: "ApprovedParts",
    color: "rgba(76, 175, 80, 0.85)",
    borderColor: "rgba(76, 175, 80, 1)",
    cardColor: "green-darken-1",
    translationKey: "part_status.approved_parts",
  },
  {
    key: "InsertedInventory",
    label: "InsertedInventory",
    color: "rgba(0, 150, 136, 0.85)",
    borderColor: "rgba(0, 150, 136, 1)",
    cardColor: "teal-darken-1",
    translationKey: "part_status.inserted_inventory",
  },
];

export default {
  components: { chart: Chart },
  data: () => ({
    users: [],
    usersLoading: false,
    selectedUserId: null,
    month: currentMonthString(),
    summary: {
      loading: false,
      totalsByStatus: null,
      total: 0,
      user: null,
    },
    error: null,
    chartKey: 0,
  }),
  computed: {
    userItems() {
      return this.users.map((user) => ({
        ...user,
        displayName:
          `${user.name || ""} ${user.surname || ""}`.trim() ||
          user.email ||
          user._id,
      }));
    },
    canLoad() {
      return Boolean(this.selectedUserId && this.month && !this.summary.loading);
    },
    formattedMonth() {
      if (!this.month) return "";
      const date = parseLocalMonthString(this.month);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
      });
    },
    selectedMonthLabel() {
      return this.formattedMonth;
    },
    monthNumber: {
      get() {
        if (!this.month) return new Date().getMonth() + 1;
        return Number(this.month.split("-")[1]);
      },
      set(value) {
        const year = this.yearNumber;
        this.month = `${year}-${String(value).padStart(2, "0")}`;
      },
    },
    yearNumber: {
      get() {
        if (!this.month) return new Date().getFullYear();
        return Number(this.month.split("-")[0]);
      },
      set(value) {
        const month = String(this.monthNumber).padStart(2, "0");
        this.month = `${value}-${month}`;
      },
    },
    monthItems() {
      const locale = this.$i18n?.locale;
      return Array.from({ length: 12 }, (_, index) => {
        const title = new Date(2000, index, 1).toLocaleDateString(locale, {
          month: "long",
        });
        return {
          title: title.charAt(0).toUpperCase() + title.slice(1),
          value: index + 1,
        };
      });
    },
    yearItems() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, index) => {
        const year = currentYear - index;
        return { title: String(year), value: year };
      });
    },
    hasData() {
      return this.summary.totalsByStatus !== null;
    },
    summaryCards() {
      const totals = this.summary.totalsByStatus || {};
      return STATUS_SERIES.map((series) => ({
        key: series.key,
        label: this.$t(series.translationKey),
        value: totals[series.key] ?? 0,
        color: series.cardColor,
      }));
    },
    allSummaryCards() {
      return [
        ...this.summaryCards,
        {
          key: "total",
          label: this.$t("home.admin_summary.total"),
          value: this.summary.total ?? 0,
          color: "grey-darken-3",
        },
      ];
    },
    barChartData() {
      const totals = this.summary.totalsByStatus || {};
      return {
        labels: STATUS_SERIES.map((s) => s.label),
        datasets: [
          {
            label: "Count",
            data: STATUS_SERIES.map((s) => totals[s.key] ?? 0),
            backgroundColor: STATUS_SERIES.map((s) => s.color),
            borderColor: STATUS_SERIES.map((s) => s.borderColor),
            borderWidth: 1,
          },
        ],
      };
    },
    barChartHeight() {
      return this.$vuetify.display.xs ? 320 : 280;
    },
    barChartExtraOptions() {
      return {
        legend: { display: false },
        scales: {
          xAxes: [
            {
              ticks: {
                maxRotation: this.$vuetify.display.xs ? 90 : 45,
                minRotation: this.$vuetify.display.xs ? 45 : 0,
                autoSkip: false,
                fontSize: this.$vuetify.display.xs ? 9 : 11,
              },
            },
          ],
          yAxes: [
            {
              ticks: { beginAtZero: true, precision: 0 },
            },
          ],
        },
      };
    },
  },
  mounted() {
    this.fetchUsers();
  },
  methods: {
    getApiTime() {
      const monthValue =
        typeof this.month === "string" && this.month.length === 7
          ? `${this.month}-01`
          : this.month;
      return moment(monthValue, "YYYY-MM-DD").startOf("month").toISOString();
    },
    async fetchUsers() {
      const role = this.$store.getters.role;
      if (role !== "LekoAdmin" && role !== "SuperAdmin") return;

      this.usersLoading = true;
      try {
        const response = await getRequest(
          "/user/",
          `role=${role}&query_role=LekoEmployee&institution_id=${this.$store.getters.institutionId}`,
        );
        this.users = response.users || [];
      } catch {
        this.users = [];
      } finally {
        this.usersLoading = false;
      }
    },
    async fetchSummary() {
      const role = this.$store.getters.role;
      if (role !== "LekoAdmin" && role !== "SuperAdmin") return;
      if (!this.selectedUserId) {
        this.error = this.$t("home.user_monthly_summary.no_user");
        return;
      }

      this.summary.loading = true;
      this.error = null;
      try {
        const response = await getRequest(
          `/history/admin/user-monthly-summary/${this.selectedUserId}`,
          `status=${DIGITALIZATION_STATUSES}&time=${this.getApiTime()}`,
        );
        this.summary.totalsByStatus = response.totalsByStatus || {};
        this.summary.total = response.total ?? 0;
        this.summary.user = response.user || null;
        this.chartKey += 1;
      } catch (err) {
        this.summary.totalsByStatus = null;
        this.summary.total = 0;
        this.summary.user = null;
        if (err?.response?.status === 403) {
          this.error = this.$t("home.user_monthly_summary.not_authorized");
        } else {
          this.error = this.$t("home.user_monthly_summary.error");
        }
      } finally {
        this.summary.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.user-summary-period {
  font-size: 0.8rem;
  font-weight: 400;
  opacity: 0.75;
}

.summary-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 8px;
}

.summary-stat-card {
  height: 100%;
  min-height: 92px;
}

.summary-stat-card .v-card-text {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.summary-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: white;
}

.summary-stat-label {
  margin-top: 4px;
  font-size: 0.8rem;
  line-height: 1.25;
  color: white;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.user-chart-wrap {
  position: relative;
  width: 100%;
  min-height: 280px;
}

@media (max-width: 600px) {
  .summary-stat-value {
    font-size: 1.25rem;
  }

  .summary-stat-label {
    font-size: 0.72rem;
  }
}
</style>
